// src/api.js
import axios from "axios";

/* ---------------------------- environment helpers ---------------------------- */
const isBrowser = typeof window !== "undefined";

const readApiOverrideOnce = () => {
  if (!isBrowser) return "";
  try {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get("api"); // e.g. ?api=http://192.168.1.50:8080
    if (qp && qp.trim()) {
      localStorage.setItem("API_URL", qp.trim());
      // optional: clean the URL
      url.searchParams.delete("api");
      window.history.replaceState({}, "", url.pathname + url.search);
      return qp.trim();
    }
  } catch {}
  return "";
};

const getSavedApiOverride = () => {
  if (!isBrowser) return "";
  // ❌ deploy pe localStorage override disable
  if (window.location.hostname !== "localhost") return "";
  try {
    const saved = localStorage.getItem("API_URL");
    return saved ? saved.trim() : "";
  } catch {}
  return "";
};

const getViteEnvApi = () => {
  try {
    return (import.meta?.env?.VITE_API_URL || "").trim();
  } catch {
    return "";
  }
};

const isLocalhostHost = () => {
  if (!isBrowser) return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
};

/* ------------------------- compute baseURL & SERVER_URL ----------------------- */
function computeApiEndpoints() {
  const qpOverride = readApiOverrideOnce();
  const lsOverride = getSavedApiOverride();
  const viteEnv = getViteEnvApi();

  // Priority: URL > localStorage > VITE > default(dev)/same-origin(prod)
  const chosenOrigin =
    qpOverride ||
    lsOverride ||
    viteEnv ||
   process.env.REACT_APP_API_BASE_URL ||
(isBrowser && isLocalhostHost() ? "http://localhost:8080" : "");

  // axios base: absolute if chosenOrigin set, else same-origin '/api'
  const baseURL = chosenOrigin
    ? `${chosenOrigin.replace(/\/+$/, "")}/api`
    : "/api";

  // sockets/uploads absolute origin ('' means same-origin)
  const originForAbsolute = chosenOrigin;

  return { originForAbsolute, baseURL };
}

const { originForAbsolute, baseURL } = computeApiEndpoints();

/* ------------------------------ public exports ------------------------------- */
export const SERVER_URL = originForAbsolute;

export const api = axios.create({
  baseURL,
  withCredentials: false,
  timeout: 20000,
});

/* ------------------------------- auth header -------------------------------- */
api.interceptors.request.use((cfg) => {
  try {
    if (isBrowser) {
      const t = localStorage.getItem("token");
      if (t) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${t}`;
      }
    }
  } catch {}
  return cfg;
});

/* --------------------------- optional error normalizer ----------------------- */
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const data = err.response?.data;
    const message =
      data?.message ||
      err.message ||
      (status ? `Request failed (${status})` : "Network error");
    return Promise.reject(new Error(message));
  }
);

/* --------------------------------- helpers ---------------------------------- */
export function setApiOrigin(origin) {
  if (!isBrowser) return;
  localStorage.setItem("API_URL", origin.trim());
  window.location.reload();
}

export function clearApiOrigin() {
  if (!isBrowser) return;
  localStorage.removeItem("API_URL");
  window.location.reload();
}

export default api;
